import { capitalize } from "lodash";
import { DATE_ONLY_PATTERN } from "@/lib/dateHelpers";
import type { AchievementReportNode, AchievementReportRow, DateRangeFilterInput, SectionLookup } from "./types";

export const MEMBER_STATUS_FILTER_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "suspended", label: "Suspended" },
  { value: "terminated", label: "Terminated" }
];

export const RECOGNITION_YEAR_FILTER_START = 1916;

export const DEFAULT_ACHIEVEMENT_TYPE_CODE = "ADM";
export const DEFAULT_ACHIEVEMENT_TYPE_TITLE = "American Development Model";

type AchievementTypeMatch = {
  title: string;
  ccmsAttributeType?: string | null;
  codes?: Array<{ code?: string | null }>;
};

function normalizeAchievementCode(value: string | null | undefined): string {
  return value?.trim().toUpperCase() ?? "";
}

export function findDefaultAchievementType<T extends AchievementTypeMatch>(types: T[] = []): T | undefined {
  const byTitle = types.find((type) => type.title.toLowerCase().includes(DEFAULT_ACHIEVEMENT_TYPE_TITLE.toLowerCase()));
  if (byTitle) return byTitle;

  return types.find((type) => {
    if (normalizeAchievementCode(type.ccmsAttributeType) === DEFAULT_ACHIEVEMENT_TYPE_CODE) {
      return true;
    }

    return type.codes?.some((code) => normalizeAchievementCode(code.code) === DEFAULT_ACHIEVEMENT_TYPE_CODE);
  });
}

export function buildAchievementReportRowId(row: AchievementReportNode): string {
  return [
    row.pgaId,
    row.recognizedAt ?? "",
    row.title ?? "",
    row.recognitionYear ?? "",
    row.reportingEntity ?? ""
  ].join("|");
}

export function mapAchievementReportRows(nodes: AchievementReportNode[] = []): AchievementReportRow[] {
  return nodes.map((node) => ({
    ...node,
    id: buildAchievementReportRowId(node)
  }));
}

export function hasNestedAchievementFields(row: AchievementReportNode): boolean {
  return !!(row.recognitionYear || row.reportingEntity || row.expiresOn || row.remarks);
}

export function buildSectionLookup(
  sections: { id: string; legacyId?: string | null; name: string }[] = []
): SectionLookup {
  const byId = new Map<string, string>();
  const byLegacyId = new Map<string, string>();

  sections.forEach((section) => {
    byId.set(section.id, section.name);
    if (section.legacyId) {
      byLegacyId.set(section.legacyId, section.name);
    }
  });

  return { byId, byLegacyId };
}

export function resolveSectionName(sectionId: string | null | undefined, lookup: SectionLookup): string | undefined {
  if (!sectionId) return undefined;
  return lookup.byId.get(sectionId) ?? lookup.byLegacyId.get(sectionId);
}

export function formatMemberStatus(status: string | null | undefined): string | null {
  if (!status || status.toLowerCase() === "unknown") {
    return null;
  }

  return capitalize(status);
}

export function buildRecognitionYearOptions({
  currentYear = new Date().getFullYear(),
  extraYears = []
}: {
  currentYear?: number;
  extraYears?: string[];
} = {}): Array<{ value: string; label: string }> {
  const years = new Set<string>();

  extraYears.forEach((year) => {
    if (year) years.add(year);
  });

  for (let year = currentYear; year >= RECOGNITION_YEAR_FILTER_START; year -= 1) {
    years.add(String(year));
  }

  return [...years].sort((left, right) => Number(right) - Number(left)).map((year) => ({ value: year, label: year }));
}

export function parseDateRangeParam(value: unknown): DateRangeFilterInput | undefined {
  if (typeof value !== "string") return undefined;

  const [from, to] = value.split(",");
  if (!from && !to) return undefined;

  return {
    ...(from && { from }),
    ...(to && { to })
  };
}

export function serializeDateRangeParam(from: string | null, to: string | null): string {
  return `${from ?? ""},${to ?? ""}`;
}

export function toDateParam(value: Date | string | null | undefined): string | null {
  if (!value) return null;

  if (typeof value === "string") {
    const dateOnly = value.split("T")[0];
    return DATE_ONLY_PATTERN.test(dateOnly) ? dateOnly : null;
  }

  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function isDateRangeValue(
  value: unknown
): value is { startDate: Date | string | null; endDate: Date | string | null } {
  return typeof value === "object" && value !== null && "startDate" in value && "endDate" in value;
}
